import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, modalStyles } from '../../components/Modal';
import { Select } from '../../components/Select';
import { api } from '../../lib/api';
import { useToast } from '../../lib/toast';
import type { Creator } from '../../lib/types';
import styles from './Dashboard.module.css';

const PERSONAS = [
  'Peak Performers Under Pressure',
  'Wired-but-Weary Wellness Seekers',
  'High-Achieving Students',
  'Parents of Young or Neurodivergent Kids',
  'Sensory-Sensitive Strivers',
];

export function AddCreatorModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [persona, setPersona] = useState('');
  const [socialLink, setSocialLink] = useState('');
  const [contact, setContact] = useState('');
  const qc = useQueryClient();
  const toast = useToast();

  useEffect(() => {
    if (!open) {
      setName('');
      setPersona('');
      setSocialLink('');
      setContact('');
    }
  }, [open]);

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.post<Creator>('/api/admin/prospects', body),
    onSuccess: (created) => {
      qc.setQueryData<Creator[]>(['admin', 'prospects'], (prev) =>
        prev ? [...prev, created] : [created],
      );
      toast('Creator added — add platforms from the drawer.');
      onClose();
    },
    onError: () => toast('Error adding'),
  });

  function submit() {
    if (!name.trim()) {
      toast('Name is required');
      return;
    }
    create.mutate({
      name: name.trim(),
      persona,
      socialLink: socialLink.trim(),
      contact: contact.trim(),
      action: 'Follow Up',
      outreachDate: '',
      outreachChannel: '',
      response: '',
      followUpDate: '',
      followUpResponse: '',
      notes: '',
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add New Creator"
      footer={
        <>
          <button className={modalStyles.ghost} onClick={onClose} type="button">
            Cancel
          </button>
          <button
            className={modalStyles.primary}
            onClick={submit}
            type="button"
            disabled={create.isPending}
          >
            {create.isPending ? 'Adding…' : 'Add Creator'}
          </button>
        </>
      }
    >
      <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 14, lineHeight: 1.5 }}>
        Add basic info now. You can attach platforms (TikTok, Instagram, etc.) from the creator
        drawer after this.
      </p>
      <div className={styles.fgrid}>
        <div className={`${styles.fitem} ${styles.fitemFull}`}>
          <label>
            Name <span style={{ color: 'var(--red)' }}>*</span>
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Creator name"
            autoFocus
          />
        </div>
        <div className={`${styles.fitem} ${styles.fitemFull}`}>
          <label>Persona</label>
          <Select
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            placeholder="Select persona…"
          >
            {PERSONAS.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </Select>
        </div>
        <div className={`${styles.fitem} ${styles.fitemFull}`}>
          <label>Social Link</label>
          <input
            value={socialLink}
            onChange={(e) => setSocialLink(e.target.value)}
            placeholder="https://"
          />
        </div>
        <div className={`${styles.fitem} ${styles.fitemFull}`}>
          <label>Contact Email</label>
          <input value={contact} onChange={(e) => setContact(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}
